import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/primeng';
import { Observable } from 'rxjs/Observable';
import { StoreModel } from '../../../models/store-common.model';
import { StoreCommonService } from '../../../services/store/store-common.service';
import { StoreCommonBasicComponent } from './store-common-basic/store-common-basic.component';
import { StoreCommonLinkComponent } from './store-common-link/store-common-link.component';
import { StoreCommonClassificationComponent } from './store-common-classification/store-common-classification.component';

@Component({
  selector: 'app-store-common-form',
  templateUrl: './store-common-form.component.html',
  providers: [],
})

export class StoreCommonFormComponent implements OnInit {
  @ViewChild(StoreCommonBasicComponent) storeBasic: StoreCommonBasicComponent;
  @ViewChild(StoreCommonLinkComponent) storeLink: StoreCommonLinkComponent;
  @ViewChild(StoreCommonClassificationComponent) storeClassification: StoreCommonClassificationComponent;

  storeId: number;
  editMode: boolean;
  activeIndex: number;
  validationForm: boolean;
  items: MenuItem[];
  invalidSubmit: boolean;

  constructor(
    private notification: MessageService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private srvStore: StoreCommonService,
  ) {}

  ngOnInit(): void {
    this.storeId = this.route.snapshot.params['store_id'];
    this.editMode = this.storeId !== undefined;

    this.validationForm = true;
    this.activeIndex = 0;

    this.translate.get([
      'common.storeData',
      'common.storeLink',
      'common.storeServices',
      'common.storeCalendars',
      'store.storeClassifications'
    ]).subscribe(res => {
      this.items = [{
        label: res['common.storeData'],
        command: (event: any) => {
          this.activeIndex = 0;
        }
      }];

      this.items.push(
        {
          label: res['common.storeLink'],
          command: (event: any) => {
            this.activeIndex = 1;
          }
        },
        {
          label: res['common.storeServices'],
          command: (event: any) => {
            this.activeIndex = 2;
          },
          disabled: !this.editMode
        },
        {
          label: res['common.storeCalendars'],
          command: (event: any) => {
            this.activeIndex = 3;
          },
          disabled: !this.editMode
        },
        {
          label: res['store.storeClassifications'],
          command: (event: any) => {
            this.activeIndex = 4;
          },
        }
      );

    });

    let store: Observable<StoreModel>;

    if (!this.editMode) {
      store = Observable.of(new StoreModel());
    } else {
      store = this.srvStore.get(this.storeId);
    }

    Observable.zip(store)
      .subscribe(([res]) => {
        this.storeBasic.createForm(res);
        this.storeLink.createForm(res);
      });

  }

  /**
   * Save method for saving both Basic customer data and Address data.
   */
  save() {

    if (!this.storeBasic.formBasic.valid) {
      this.invalidSubmit = true;
      this.storeBasic.invalidSubmit = true;
      this.activeIndex = 0;
      return;
    }

    if (!this.storeLink.formLink.valid) {
      this.invalidSubmit = true;
      this.activeIndex = 1;
      this.storeLink.invalidSubmit = true;
      return;
    }

    const store = new StoreModel();

    store.address_1 = this.storeBasic.formBasic.get('address_1').value;
    store.address_2 = this.storeBasic.formBasic.get('address_2').value;
    store.address_number = this.storeBasic.formBasic.get('address_number').value;
    store.altitude = this.storeBasic.formBasic.get('altitude').value;
    store.channel_loyalty_id = this.storeBasic.formBasic.get('channel_loyalty_id').value;
    store.city = this.storeBasic.formBasic.get('city').value;
    store.code = this.storeBasic.formBasic.get('code').value;
    store.contact_1 = this.storeBasic.formBasic.get('contact_1').value;
    store.contact_2 = this.storeBasic.formBasic.get('contact_2').value;
    store.country_id = this.storeBasic.formBasic.get('country_id').value;
    store.date_start = this.storeBasic.formBasic.get('date_start').value;
    store.date_end = this.storeBasic.formBasic.get('date_end').value;
    store.description = this.storeBasic.formBasic.get('description').value;
    store.division_sales_network_id = this.storeBasic.formBasic.get('division_sales_network_id').value;
    store.email = this.storeBasic.formBasic.get('email').value;
    store.fax = this.storeBasic.formBasic.get('fax').value;
    store.host_code = this.storeBasic.formBasic.get('host_code').value;
    store.latitude = this.storeBasic.formBasic.get('latitude').value || null;
    store.longitude = this.storeBasic.formBasic.get('longitude').value || null;
    store.name = this.storeBasic.formBasic.get('name').value;
    store.note_1 = this.storeBasic.formBasic.get('note_1').value;
    store.note_2 = this.storeBasic.formBasic.get('note_2').value;
    store.note_3 = this.storeBasic.formBasic.get('note_3').value;
    store.province_id = this.storeBasic.formBasic.get('province_id').value;
    store.short_name = this.storeBasic.formBasic.get('short_name').value;
    store.state_id = this.storeBasic.formBasic.get('state_id').value;
    store.telephone_1 = this.storeBasic.formBasic.get('telephone_1').value;
    store.telephone_2 = this.storeBasic.formBasic.get('telephone_2').value;
    store.telephone_3 = this.storeBasic.formBasic.get('telephone_3').value;
    store.toponym_id = this.storeBasic.formBasic.get('toponym_id').value;
    store.zip_code = this.storeBasic.formBasic.get('zip_code').value;

    store.flg_graphical_printer = this.storeLink.formLink.get('flg_graphical_printer').value;
    store.flg_campaign_candidate = this.storeLink.formLink.get('flg_campaign_candidate').value;
    store.ip_address = this.storeLink.formLink.get('ip_address').value;
    store.ext_flyer_url = this.storeLink.formLink.get('ext_flyer_url').value;

    store.checkouts_number = this.storeBasic.formBasic.get('checkouts_number').value;
    store.store_surface = this.storeBasic.formBasic.get('store_surface').value;


    if (!this.editMode) {
      this.srvStore.post(store).subscribe(res => {
        this.storeId = res.id;
        this.storeClassification.save(this.storeId);
        this.translate.get(['common.ok', 'common.save_ok']).subscribe(data => {
          this.notification.add({
            severity: 'information',
            detail: data['common.save_ok']
          })
        });
        this.router.navigate(['/store-common']);
      });
    } else {
      this.srvStore.put(this.storeId, store).subscribe( () => {
        this.storeClassification.save(this.storeId);
        this.translate.get(['common.ok', 'common.update_ok']).subscribe(data => {
          this.notification.add({
            severity: 'information',
            detail: data['common.update_ok']
          })
        });
        this.router.navigate(['/store-common']);
      });
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/views/administration/store/store-common-form.component.ts